/**
 * A mesa SEM conversa aberta — e é aqui que ela deixa de ser tela de espera.
 *
 * `/atendimentos` sem id mostrava a saudação e três números em texto: uma tela
 * de boas-vindas, num lugar onde a pessoa passa dezenas de vezes por dia (é onde
 * cai quem fecha uma conversa, quem recarrega, quem chega pelo trilho). A coluna
 * da esquerda tinha o filtro de verdade — a matriz etapa × responsável — em
 * 380px, com os rostos numa fileira de sete e o funil numa barra de 26px de
 * altura; e 1000px de largura ao lado disso ficavam com um "Bom dia".
 *
 * Agora esta é a tela do recorte, em quatro visões (`?visao=`):
 *
 *   - **resumo** (o padrão): a saudação e o próximo passo, que é o que serve a
 *     quem acabou de entrar. Continua sendo `BoasVindas`.
 *   - **painel**: a matriz etapa × responsável ABERTA, com a largura toda — a
 *     mesma contagem do filtro da coluna, em tamanho de ler. É o "dashboard": os
 *     números vêm do banco (`matrizDaMesa`), nunca da página carregada.
 *   - **kanban**: os casos em colunas por etapa, para arrumar o dia olhando o
 *     funil em vez da ordem da última mensagem.
 *   - **tabela**: as mesmas linhas em grade densa, para varrer muitos casos de
 *     uma vez (quem é, onde está, de quem é, o que espera).
 *
 * As três últimas leem o MESMO recorte da lista (`?q=`, `?filtro=`, `?etapa=`,
 * `?resp=`, `?tag=`, `?grupo=`) — mudar a visão não muda o que se está olhando,
 * só a forma. E a visão mora na URL: "olha o kanban dos meus" se manda por
 * mensagem, e recarregar cai no mesmo lugar.
 *
 * O que ELAS NÃO FAZEM é contar: kanban e tabela desenham a página carregada
 * (200) e dizem isso no pé, como a lista já faz. Toda contagem grande vem do
 * servidor — foi o erro que a lista da mesa já cometeu uma vez.
 */
import { useMemo, useState } from "react";
import * as api from "../api.js";
import { ICONES, Icone, corAvatar, dataHora, plural, quandoCurto, tinta } from "../visual.jsx";
import { useAcoes, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { chaveDaEtiqueta, coresDoCatalogo } from "../etiquetas.js";
import { Avatar, Botao, Esqueleto, Etiqueta, Fichas, MenuDeAcoes, Retrato, Rolagem, Vazio } from "../componentes/comuns.jsx";
import { Button } from "@/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/table";
import { confirmar } from "@/componentes/pergunta.tsx";
import BoasVindas from "./BoasVindas.jsx";
import { SEM_RESPONSAVEL, chaveNaMatriz, contarPainel } from "../mesa-painel.js";
import { IDS_DAS_VISOES, VISAO_DE_FABRICA, lerVisaoPadrao, salvarVisaoPadrao } from "../mesa-visao.js";

/**
 * As quatro visões, na ordem em que a fileira as escreve.
 *
 * O rótulo e o ícone são daqui; os IDS vêm de `mesa-visao.js`, que é quem guarda
 * a preferência — duas listas de ids drifta num dos dois arquivos, e a que
 * envelheceria é a que decide qual tela a mesa abre.
 */
const ROTULO_DA_VISAO = {
  resumo: { rotulo: "Resumo", icone: ICONES.brilho },
  painel: { rotulo: "Painel", icone: ICONES.matriz },
  kanban: { rotulo: "Kanban", icone: ICONES.fluxos },
  tabela: { rotulo: "Tabela", icone: ICONES.tarefas },
};
export const VISOES = IDS_DAS_VISOES.map((id) => ({ id, ...ROTULO_DA_VISAO[id] }));

/**
 * O que "limpar o recorte" apaga — as cinco chaves do recorte da mesa.
 *
 * Uma lista só, porque três lugares a usam (o botão do cabeçalho e as duas
 * listas vazias): recorte limpo pela metade é a tela dizendo "sem filtro" com
 * um filtro de pé.
 */
const RECORTE_LIMPO = { grupo: null, etapa: null, resp: null, tag: null, criativo: null, q: null };

/** A visão pedida, ou a fixada por quem está olhando. Fora daqui ninguém decide isso. */
const visaoValida = (pedida) => (VISOES.some((v) => v.id === pedida) ? pedida : lerVisaoPadrao());

/* ============================================================ os três grupos */

/**
 * Os TRÊS grupos do funil, em cartão grande, no alto de toda visão.
 *
 * É o meta-agrupamento (`GRUPOS_DA_MESA`, em src/leads.ts): as dez etapas
 * respondendo a pergunta grossa — isto não começou, está andando, ou acabou? Com
 * dez segmentos essa resposta era uma soma de cabeça, e é a que se faz vinte
 * vezes por dia.
 *
 * Cada cartão é o RECORTE de que ele é a contagem (`?grupo=`), como os números
 * da saudação: número que não leva a lugar nenhum é texto morto. O corte das
 * etapas vem do servidor — a tela não tem cópia dele.
 */
function Grupos() {
  /* A matriz inteira: escolher um grupo não pode zerar os outros dois cartões. */
  const { matrizInteira: matriz, etapas, gruposDaMesa } = useEstado();
  const { grupoDaMesa, ajustar } = useNavegacao();

  const contados = useMemo(() => {
    const grupoDe = new Map((etapas ?? []).map((e) => [e.chave, e.grupo]));
    const soma = new Map();
    for (const c of matriz) {
      const g = grupoDe.get(c.stage);
      if (g) soma.set(g, (soma.get(g) ?? 0) + c.n);
    }
    return (gruposDaMesa ?? []).map((g) => ({ ...g, n: soma.get(g.chave) ?? 0 }));
  }, [matriz, etapas, gruposDaMesa]);

  if (!contados.length) return null;

  return (
    <div className="grid grid-cols-3 gap-2.5 max-[759px]:grid-cols-1">
      {contados.map((g) => {
        const marcado = grupoDaMesa === g.chave;
        return (
          <button
            key={g.chave}
            type="button"
            onClick={() => ajustar({ grupo: marcado ? null : g.chave })}
            aria-pressed={marcado}
            aria-label={`${g.rotulo}, ${g.n} atendimentos`}
            className={`flex cursor-pointer flex-col items-start gap-1 rounded-xl border p-[14px_16px] text-left transition-colors duration-150 ${
              marcado ? "border-foreground bg-foreground text-creme" : "border-border bg-card hover:border-foreground/28"
            }`}
          >
            <span
              className={`text-selo font-bold tracking-[.08em] uppercase ${marcado ? "text-creme/70" : "text-muted-foreground"}`}
            >
              {g.rotulo}
            </span>
            {/* O número grande é o que responde de relance — é o idioma dos
                widgets da coluna do caso, e por isso ele é o corpo do cartão e
                não uma nota no cabeçalho. */}
            <span className="font-serif text-vista leading-none">{g.n}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ================================================================== o painel */

/*
 * A COR de cada meta-grupo, e por que estas três.
 *
 * O grupo responde uma pergunta de tempo, não de qualidade: isto ainda não
 * começou, está andando, ou acabou? Âmbar é o que espera (o mesmo âmbar da nota
 * interna e da espera), terra é o que está em trabalho (a cor de AÇÃO da casa) e
 * verde é o que fechou. Token e nunca hexadecimal — a guarda barra, e com razão.
 */
const COR_DO_GRUPO = {
  aberto: "var(--color-ambar)",
  atendendo: "var(--color-terra)",
  concluido: "var(--color-verde)",
};
const COR_PADRAO_DO_GRUPO = "var(--color-terra)";

/*
 * A intensidade da célula: quanto do total daquela COLUNA está nela.
 *
 * Proporção e não valor absoluto, porque as três colunas têm ordens de grandeza
 * diferentes — "Concluído" acumula o ano inteiro e afogaria "Em aberto" numa
 * escala única, que é o heatmap mentindo sobre onde está o trabalho. Cada coluna
 * se compara consigo mesma.
 *
 * Piso de 14%: célula com caso precisa ser VISÍVEL como célula com caso, mesmo
 * valendo 2% da coluna. Teto de 78% para a letra continuar legível por cima.
 */
const PISO_DA_COR = 14;
const TETO_DA_COR = 78;
/* Acima disto o fundo é escuro demais para a tinta: a letra vira creme. */
const ONDE_A_LETRA_VIRA_CREME = 52;

function forcaDaCelula(n, maiorDaColuna) {
  if (!n) return 0;
  if (!maiorDaColuna) return PISO_DA_COR;
  const bruta = (n / maiorDaColuna) * TETO_DA_COR;
  return Math.max(PISO_DA_COR, Math.min(TETO_DA_COR, Math.round(bruta)));
}

/** O véu da cor do grupo, na força pedida. `color-mix` porque token literal o
    Tailwind congela na compilação — e véu congelado não acompanha o escuro. */
const veuDoGrupo = (grupo, forca) =>
  forca ? `color-mix(in oklab, ${COR_DO_GRUPO[grupo] ?? COR_PADRAO_DO_GRUPO} ${forca}%, transparent)` : "transparent";

/**
 * O painel: quem cuida × em que PÉ está o caso, com a largura toda.
 *
 * Era uma coluna por ETAPA — as dez do funil. Duas coisas quebravam ali. A
 * pergunta que se faz olhando esta grade é "quem está sobrecarregado e com o
 * quê", e dez colunas de 92px respondem isso com uma soma de cabeça por linha;
 * pior, etapa é configurável em tempo de execução, então a grade mudava de
 * largura quando o escritório criava uma etapa. O meta-grupo
 * (`GRUPOS_DA_MESA`, em src/leads.ts) é a mesma informação na altura em que a
 * decisão acontece: não começou, está andando, acabou.
 *
 * Com três colunas sobra largura, e é isso que paga o HEATMAP: a célula deixou
 * de ser um número solto numa grade cinza e passou a ter peso de cor. Quem varre
 * a grade vê onde está a carga antes de ler um algarismo — que é a coisa que a
 * grade existe para responder.
 *
 * O corte etapa → grupo vem do SERVIDOR (`etapas[].grupo`), como em `Grupos()`:
 * duas respostas para "isto está andando?" é como uma delas envelhece.
 *
 * `Table` do registro e não grade à mão: cabeçalho de coluna e de linha
 * (`scope`) é o que faz o leitor de tela dizer "Camila, Atendendo, 4" em vez de
 * ler doze números soltos.
 */
function Painel() {
  const { matrizInteira, etapas, equipe, gruposDaMesa } = useEstado();
  const { grupoDaMesa, respDaMesa, ajustar } = useNavegacao();

  /* A matriz INTEIRA e a ordem pelo nome: o clique marca e estreita a lista,
     mas não rearruma nem apaga o mapa — ver `mesa-painel.js`. */
  const { linhas, colunas, porCelula, maiorPorColuna, totalGeral } = useMemo(
    () => contarPainel({ matriz: matrizInteira, etapas, equipe, grupos: gruposDaMesa }),
    [matrizInteira, etapas, equipe, gruposDaMesa],
  );

  if (!colunas.length) return <Esqueleto linhas={6} altura={38} rotulo="Contando a mesa…" />;

  const irPara = (grupo, resp) =>
    ajustar({
      grupo: grupoDaMesa === grupo && respDaMesa === resp ? null : grupo,
      resp: grupoDaMesa === grupo && respDaMesa === resp ? null : resp,
      /* A etapa sai do recorte: a célula é do GRUPO, e deixar uma etapa velha
         marcada mostraria uma contagem que não é a do número clicado. */
      etapa: null,
    });

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <Table className="text-apoio">
        <TableHeader>
          <TableRow>
            <TableHead className="sticky left-0 z-[1] min-w-[170px] bg-card">Quem cuida</TableHead>
            {colunas.map((g) => (
              <TableHead key={g.chave} className="min-w-[132px] text-center align-bottom">
                <button
                  type="button"
                  onClick={() => ajustar({ grupo: grupoDaMesa === g.chave ? null : g.chave, resp: null, etapa: null })}
                  aria-pressed={grupoDaMesa === g.chave && !respDaMesa}
                  className="flex w-full cursor-pointer flex-col items-center gap-1 border-none bg-transparent p-0 text-inherit"
                >
                  {/* A tarja da cor do grupo: é a legenda do heatmap, e fica no
                      alto da coluna que ela pinta — legenda separada da grade é
                      a que ninguém lê. */}
                  <span
                    className="h-[5px] w-full rounded-full"
                    style={{ background: COR_DO_GRUPO[g.chave] ?? COR_PADRAO_DO_GRUPO }}
                  />
                  <span className="text-selo leading-[1.25] text-balance whitespace-normal uppercase tracking-[.06em]">
                    {g.rotulo}
                  </span>
                  <span className="font-mono text-corpo font-bold">{g.total}</span>
                </button>
              </TableHead>
            ))}
            <TableHead className="min-w-[64px] text-center">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {linhas.map((p) => (
            <TableRow key={p.chave}>
              <TableHead scope="row" className="sticky left-0 z-[1] bg-card font-normal">
                <button
                  type="button"
                  onClick={() => ajustar({ resp: respDaMesa === p.chave ? null : p.chave, grupo: null, etapa: null })}
                  aria-pressed={respDaMesa === p.chave && !grupoDaMesa}
                  className="flex w-full min-w-0 cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left text-inherit"
                >
                  {p.chave === SEM_RESPONSAVEL ? (
                    <span aria-hidden="true" className="size-5 flex-none rounded-full border border-dashed border-terra/45" />
                  ) : (
                    <Avatar nome={p.nome} foto={p.foto} cor={p.cor} tamanho={20} fonte={8.5} />
                  )}
                  <span className="min-w-0 flex-1 truncate text-corpo">{p.nome}</span>
                </button>
              </TableHead>
              {colunas.map((g) => {
                const n = porCelula.get(`${g.chave}|${chaveNaMatriz(p.chave)}`) ?? 0;
                const marcada = grupoDaMesa === g.chave && respDaMesa === p.chave;
                const forca = forcaDaCelula(n, maiorPorColuna.get(g.chave) ?? 0);
                return (
                  <TableCell key={g.chave} className="p-[3px] text-center">
                    <button
                      type="button"
                      onClick={() => irPara(g.chave, p.chave)}
                      aria-pressed={marcada}
                      aria-label={`${p.nome}, ${g.rotulo}: ${n}`}
                      /* Zero recua e continua clicável: some do olho sem sumir da
                         mão — apagá-lo esconderia que a célula existe. */
                      className={`flex h-[38px] w-full cursor-pointer items-center justify-center rounded-[7px] border font-mono text-corpo transition-[background-color,border-color] duration-150 ${
                        marcada
                          ? "border-foreground bg-foreground font-bold text-creme"
                          : n
                            ? "border-transparent hover:border-foreground/30"
                            : "border-transparent text-tinta-fantasma hover:bg-foreground/4"
                      }`}
                      style={
                        marcada || !n
                          ? undefined
                          : {
                              background: veuDoGrupo(g.chave, forca),
                              /* A tinta segue o fundo: creme em cima do véu
                                 forte, tinta normal no fraco. Sem isto a célula
                                 mais carregada — justamente a que importa — é a
                                 menos legível da grade. */
                              color: forca >= ONDE_A_LETRA_VIRA_CREME ? "var(--color-creme)" : undefined,
                              fontWeight: forca >= ONDE_A_LETRA_VIRA_CREME ? 700 : undefined,
                            }
                      }
                    >
                      {n || "·"}
                    </button>
                  </TableCell>
                );
              })}
              <TableCell className="text-center font-mono text-corpo font-bold">{p.total}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="border-t border-t-border px-3 py-2 font-mono text-selo text-muted-foreground">
        {plural(totalGeral, "atendimento", "atendimentos")} no recorte · a cor é a carga dentro da coluna · contagem do banco, não
        da página carregada
      </div>
    </div>
  );
}

/* ================================================================== o kanban */

/**
 * Mover UM caso de etapa, sem sair da visão.
 *
 * Menu com as etapas e não arrasto: o arrasto de ponteiro desta casa já existe
 * duas vezes (`Tarefas.jsx` e `Documentos.jsx`, ~200 linhas cada, com captura de
 * ponteiro e laço de quadro), e um terceiro gesto é o que divergiria na primeira
 * correção. A PERGUNTA fica, porque mover etapa dispara régua e cobrança — é a
 * mesma proteção do trilho do processo.
 *
 * ponytail: sem arrasto. Se o escritório pedir, o caminho é extrair o
 * `useArrastoDoAcervo` de Documentos.jsx para a camada da casa e usá-lo nos três
 * — nunca escrever o quarto.
 */
function useMoverEtapa() {
  const { avisar, carregarMesa } = useAcoes();
  return async (caso, etapa) => {
    if (
      !(await confirmar({
        titulo: `Mover ${caso.nome} para “${etapa.rotulo}”?`,
        texto: "Mudar de etapa liga a régua de cobrança e as tarefas dela.",
        acao: "Mover",
      }))
    )
      return;
    try {
      await api.loteEtapa([caso.id], etapa.chave);
      await carregarMesa();
      avisar(`${caso.nome} está em ${etapa.rotulo}.`);
    } catch (e) {
      avisar(e.message);
    }
  };
}

/** Um caso no kanban: quem é, o que espera, de quem é. */
function Cartao({ caso, coresDasTags, etapas, aoAbrir, aoMover }) {
  const tags = caso.tags ? caso.tags.split("|").filter(Boolean) : [];
  return (
    <div className="rounded-xl border border-border bg-card p-[10px_11px] transition-colors duration-150 hover:border-foreground/28">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => aoAbrir(caso.id)}
          className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 border-none bg-transparent p-0 text-left"
        >
          <Retrato nome={caso.nome} registro={caso.id} tem={Boolean(caso.tem_avatar)} tamanho={28} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-corpo font-semibold">{caso.nome}</span>
            <span className="mt-[2px] block font-mono text-selo text-muted-foreground">
              {/* Caso sem mensagem não tem "quando": juntar as partes evita a
                  linha começar com " · na fila". */}
              {[caso.ultima_em ? quandoCurto(caso.ultima_em) : null, caso.responsavel || "na fila"].filter(Boolean).join(" · ")}
            </span>
          </span>
        </button>
        {caso.nao_lidas > 0 && (
          <span className="mt-[3px] min-w-[18px] flex-none rounded-[9px] bg-primary px-[5px] text-center text-selo font-bold text-creme">
            {caso.nao_lidas}
          </span>
        )}
        <MenuDeAcoes
          rotulo={`Mover ${caso.nome} de etapa`}
          titulo="Mover para outra etapa"
          legenda={caso.nome}
          ancora="bottom end"
          largura="240px"
          altura="340px"
          classe="mt-[1px] flex size-[24px] flex-none items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
          itens={etapas
            .filter((e) => e.chave !== caso.stage)
            .map((e) => ({ chave: e.chave, rotulo: e.rotulo, icone: ICONES.avancar, aoTocar: () => aoMover(caso, e) }))}
        >
          <Icone de={ICONES.mais} tamanho={15} largura={1.9} />
        </MenuDeAcoes>
      </div>
      {/* `Boolean`: com tese vazia e zero etiquetas, `(tese || 0) &&` desenhava
          um "0" solto no cartão. */}
      {Boolean(caso.tese || tags.length) && (
        <div className="mt-2 flex flex-wrap gap-[5px]">
          {caso.tese ? <Etiqueta discreta>{caso.tese}</Etiqueta> : null}
          {tags.slice(0, 2).map((t) => (
            <Etiqueta key={t} cores={tinta(t, coresDasTags.get(chaveDaEtiqueta(t)))}>
              {t}
            </Etiqueta>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Os casos em colunas por ETAPA — o funil com os nomes dentro.
 *
 * Coluna por etapa e não pelos três grupos: grupo é a leitura grossa (está no
 * alto, nos cartões), e um kanban de três colunas com dez etapas dentro esconde
 * justamente o que se veio ver. Rola de lado, e isso é escolha: o trilho do
 * processo não pode rolar (ele existe para mostrar o caminho inteiro), mas aqui
 * a coluna tem CONTEÚDO, e dez colunas de 240px legíveis valem mais que dez de
 * 90px onde nenhum nome cabe.
 *
 * Etapa sem caso no recorte não desenha coluna: dez colunas vazias para achar
 * duas com trabalho é o kanban trabalhando contra quem olha.
 */
function Kanban() {
  const { mesa, etapas, totalDaMesa, mesaCarregando, catalogoDeTags } = useEstado();
  const { irParaCaso, grupoDaMesa, ajustar } = useNavegacao();
  const { novoCaso } = useAcoes();
  const mover = useMoverEtapa();
  const coresDasTags = useMemo(() => coresDoCatalogo(catalogoDeTags), [catalogoDeTags]);

  const colunas = useMemo(() => {
    const porEtapa = new Map();
    for (const c of mesa) {
      const lista = porEtapa.get(c.stage);
      if (lista) lista.push(c);
      else porEtapa.set(c.stage, [c]);
    }
    return (etapas ?? [])
      .filter((e) => !grupoDaMesa || e.grupo === grupoDaMesa)
      .map((e) => ({ ...e, casos: porEtapa.get(e.chave) ?? [] }))
      .filter((e) => e.casos.length);
  }, [mesa, etapas, grupoDaMesa]);

  if (mesaCarregando && !mesa.length) return <Esqueleto linhas={4} altura={120} rotulo="Montando o kanban…" />;
  if (!colunas.length)
    return (
      <Vazio
        titulo="Nada com este recorte"
        acao={{ rotulo: "Limpar o recorte", aoTocar: () => ajustar(RECORTE_LIMPO) }}
        segunda={{ rotulo: "Abrir um atendimento", aoTocar: novoCaso }}
      >
        O kanban desenha o mesmo recorte da lista — a busca, o grupo e a célula marcada valem aqui também.
      </Vazio>
    );

  return (
    <>
      <div className="flex gap-2.5 overflow-x-auto pb-2">
        {colunas.map((e) => (
          <section key={e.chave} className="flex w-[250px] flex-none flex-col gap-2">
            <header className="flex items-center gap-2 rounded-lg bg-foreground/5 px-[10px] py-[7px]">
              <span className="size-[8px] flex-none rounded-full" style={{ background: e.cor || corAvatar(e.chave) }} />
              <h3 className="m-0 min-w-0 flex-1 truncate text-selo font-bold tracking-[.06em] uppercase">{e.rotulo}</h3>
              <span className="flex-none font-mono text-selo text-muted-foreground">{e.casos.length}</span>
            </header>
            {e.casos.map((caso) => (
              <Cartao
                key={caso.id}
                caso={caso}
                coresDasTags={coresDasTags}
                etapas={etapas ?? []}
                aoAbrir={irParaCaso}
                aoMover={mover}
              />
            ))}
          </section>
        ))}
      </div>
      <PeDaPagina quantos={mesa.length} total={totalDaMesa} />
    </>
  );
}

/* ================================================================== a tabela */

/**
 * As mesmas linhas em grade densa — para varrer muitos casos de uma vez.
 *
 * A coluna de conversas é ordenada pela última mensagem e mostra sete linhas por
 * vez; ela responde "com quem eu falo agora". A tabela responde a outra
 * pergunta: "o que existe" — trinta casos na tela, com etapa, tese, titular e
 * espera na mesma régua, para comparar em vez de abrir um por um.
 */
function Tabela() {
  const { mesa, totalDaMesa, mesaCarregando, catalogoDeTags } = useEstado();
  const { irParaCaso, ajustar } = useNavegacao();
  const { novoCaso } = useAcoes();
  const coresDasTags = useMemo(() => coresDoCatalogo(catalogoDeTags), [catalogoDeTags]);

  if (mesaCarregando && !mesa.length) return <Esqueleto linhas={10} altura={34} rotulo="Montando a tabela…" />;
  if (!mesa.length)
    return (
      <Vazio
        titulo="Nada com este recorte"
        acao={{ rotulo: "Limpar o recorte", aoTocar: () => ajustar(RECORTE_LIMPO) }}
        segunda={{ rotulo: "Abrir um atendimento", aoTocar: novoCaso }}
      >
        A tabela desenha o mesmo recorte da lista — a busca, o grupo e a célula marcada valem aqui também.
      </Vazio>
    );

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="overflow-x-auto">
        <Table className="text-apoio">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[190px]">Cliente</TableHead>
              <TableHead className="min-w-[130px]">Etapa</TableHead>
              <TableHead className="min-w-[120px]">Tese</TableHead>
              <TableHead className="min-w-[120px]">Quem cuida</TableHead>
              <TableHead className="min-w-[150px]">Etiquetas</TableHead>
              <TableHead className="min-w-[110px]">Última</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mesa.map((caso) => {
              const tags = caso.tags ? caso.tags.split("|").filter(Boolean) : [];
              return (
                /* A linha inteira abre o caso — é o alvo, como na coluna de
                   conversas. `lin` é o realce da casa. */
                <TableRow key={caso.id} className="lin cursor-pointer" onClick={() => irParaCaso(caso.id)}>
                  <TableCell className="font-semibold">
                    <span className="flex items-center gap-2">
                      <Retrato nome={caso.nome} registro={caso.id} tem={Boolean(caso.tem_avatar)} tamanho={22} />
                      <span className="min-w-0 max-w-[320px] truncate text-corpo" data-dica={caso.nome}>
                        {caso.nome}
                      </span>
                      {caso.arquivado ? (
                        <Etiqueta discreta peso={500}>
                          arquivado
                        </Etiqueta>
                      ) : null}
                      {caso.nao_lidas > 0 ? (
                        <span className="min-w-[17px] flex-none rounded-[9px] bg-primary px-[5px] text-center text-selo font-bold text-creme">
                          {caso.nao_lidas}
                        </span>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      <span
                        className="size-[7px] flex-none rounded-full"
                        style={{ background: caso.cor || corAvatar(caso.stage) }}
                      />
                      {caso.etapa}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{caso.tese || "—"}</TableCell>
                  <TableCell>{caso.responsavel || <span className="text-terra">na fila</span>}</TableCell>
                  <TableCell>
                    <span className="flex flex-wrap gap-[4px]">
                      {tags.length ? (
                        tags.slice(0, 3).map((t) => (
                          <Etiqueta key={t} cores={tinta(t, coresDasTags.get(chaveDaEtiqueta(t)))}>
                            {t}
                          </Etiqueta>
                        ))
                      ) : (
                        <span className="text-tinta-fantasma">—</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-dica={caso.ultima_em ? dataHora(caso.ultima_em) : undefined} className="font-mono text-selo">
                      {quandoCurto(caso.ultima_em)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <PeDaPagina quantos={mesa.length} total={totalDaMesa} />
    </div>
  );
}

/**
 * Quantos estão desenhados de quantos existem.
 *
 * Kanban e tabela desenham a PÁGINA (200 por vez, o mesmo teto da lista), e
 * omitir isso é a tela dizendo "estes são todos" com 328 a mais no banco — o
 * erro que a lista da mesa já cometeu. A rolagem da coluna é que puxa a página
 * seguinte, então aqui a frase aponta para ela em vez de repetir o botão.
 */
function PeDaPagina({ quantos, total }) {
  if (total == null || total <= quantos) return null;
  return (
    <div className="border-t border-t-border px-3 py-2 font-mono text-selo text-muted-foreground">
      {quantos} de {total} — role a coluna de conversas para trazer os próximos.
    </div>
  );
}

/* ============================================================ visão e o pino */

/**
 * A fileira de visões, com o PINO que escolhe qual delas a mesa abre.
 *
 * A mesa abria sempre no Resumo, e para quem trabalha no Kanban isso é um toque
 * a mais em toda chegada — dezenas por dia, para desfazer uma escolha que a
 * pessoa já fez. O pino é a escolha, feita onde ela é óbvia: ao lado da visão em
 * que se está, marcando a que está fixada.
 *
 * Um alvo só e não uma tela de preferências: fixar é "é esta", e esconder isso
 * atrás de Ajustes é o que faz ninguém saber que dá para mudar.
 */
function VisaoDaMesa({ onde, padrao, aoFixar, aoTrocar }) {
  const fixada = onde === padrao;
  const rotulo = VISOES.find((v) => v.id === onde)?.rotulo ?? onde;
  return (
    <div className="flex flex-none items-center gap-1.5">
      <Fichas rotulo="Visão da mesa" valor={onde} aoEscolher={aoTrocar} opcoes={VISOES} />
      <Button
        variant="outline"
        size="xs"
        onClick={() => aoFixar(fixada ? VISAO_DE_FABRICA : onde)}
        aria-pressed={fixada}
        /* `data-dica` explica, `aria-label` NOMEIA: botão só de ícone sem o
           segundo é "botão" e mais nada para o leitor de tela. */
        data-dica={
          fixada ? `${rotulo} é a visão que a mesa abre — toque para voltar ao Resumo` : `Abrir a mesa sempre em ${rotulo}`
        }
        aria-label={fixada ? `${rotulo} é a visão padrão da mesa. Desafixar.` : `Fixar ${rotulo} como visão padrão da mesa`}
        className={`alvo size-[30px] flex-none rounded-[9px] p-0 ${
          fixada
            ? "border-terra bg-terra/12 text-terra-escura hover:bg-terra/18 hover:text-terra-escura"
            : "border-input bg-transparent text-tinta-fantasma"
        }`}
      >
        <Icone de={ICONES.starred} tamanho={15} largura={fixada ? 2.1 : 1.7} />
      </Button>
    </div>
  );
}

/* =================================================================== a mesa */

export default function Mesa() {
  const { totalDaMesa } = useEstado();
  const { visaoDaMesa, grupoDaMesa, etapaDaMesa, respDaMesa, tagDaMesa, criativoDaMesa, termo, ajustar } = useNavegacao();
  /* A visão fixada vive no navegador; no estado para a tela repintar quando ela
     muda (o pino é apertado AQUI, então não há evento de fora para escutar). */
  const [padrao, setPadrao] = useState(lerVisaoPadrao);
  const onde = visaoValida(visaoDaMesa);
  const recortado = Boolean(grupoDaMesa || etapaDaMesa || respDaMesa || tagDaMesa || criativoDaMesa || termo);

  /* A visão PADRÃO é a ausência de `?visao=`: escolher a que já é padrão limpa o
     parâmetro em vez de escrevê-lo. Cravar "resumo" aqui deixaria o endereço
     dizendo `?visao=kanban` para quem fixou o kanban — o mesmo lugar com dois
     endereços, e o link que se manda por mensagem deixando de ser o recorte. */
  const trocarVisao = (v) => ajustar({ visao: v === padrao ? null : v });
  const fixar = (v) => {
    salvarVisaoPadrao(v);
    setPadrao(v);
    /* A URL passa a falar da visão nova: sem isto, fixar a visão em que se está
       deixaria `?visao=kanban` escrito sobre um padrão que já é kanban. */
    ajustar({ visao: onde === v ? null : visaoDaMesa || null });
  };
  const seletor = <VisaoDaMesa onde={onde} padrao={padrao} aoFixar={fixar} aoTrocar={trocarVisao} />;

  /* O resumo é tela de chegada e se apresenta sozinha: ela já tem a saudação, o
     avatar e os botões. A fileira de visões flutua no canto para não pousar em
     cima da primeira frase da mesa. */
  if (onde === "resumo")
    return (
      <div className="relative flex h-full min-w-0 flex-col overflow-hidden">
        <div className="absolute top-3 right-4 z-[2]">{seletor}</div>
        <div className="min-h-0 flex-1">
          <BoasVindas />
        </div>
      </div>
    );

  return (
    <Rolagem padding="0">
      <div className="flex min-w-0 flex-col gap-3.5 p-[18px] max-[759px]:p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            {/* Sem conversa aberta a coluna da lista tem o `h1` da mesa (ver
                `Lista.jsx`); aqui é `h2` para a tela não ter dois primeiros
                níveis. No celular a lista não está montada — mas ali esta vista
                também não aparece, porque o toque cai direto na conversa. */}
            <h2 className="m-0 font-serif text-titulo leading-tight font-normal">
              {VISOES.find((v) => v.id === onde).rotulo} da mesa
            </h2>
            <p className="m-0 mt-0.5 text-apoio text-muted-foreground">
              {totalDaMesa == null ? "Contando…" : `${plural(totalDaMesa, "atendimento", "atendimentos")} no recorte`}
              {recortado ? " · recorte da coluna ao lado" : ""}
            </p>
          </div>
          <div className="flex flex-none items-center gap-2">
            {recortado && <Botao rotulo="Limpar o recorte" icone={ICONES.fechar} aoTocar={() => ajustar(RECORTE_LIMPO)} />}
            {seletor}
          </div>
        </div>

        <Grupos />

        {onde === "painel" ? <Painel /> : onde === "kanban" ? <Kanban /> : <Tabela />}
      </div>
    </Rolagem>
  );
}
